import * as THREE from 'three';
import { ballRadius, type BallSnapshot } from '../sim/world.js';
import { createProjectedMaterial } from './vendor/ballmaterial.js';

/** プール標準球の色（id → 16進、参考画像準拠）。9-15 はテクスチャでストライプ。 */
export const BALL_COLORS: Record<number, number> = {
  0: 0xf3f1e7,
  1: 0xf2c200, 2: 0x1546a0, 3: 0xc8102e, 4: 0x4b2e83, 5: 0xe35205,
  6: 0x007a3d, 7: 0x7a1f2b, 8: 0x070707,
  9: 0xf2c200, 10: 0x1546a0, 11: 0xc8102e, 12: 0x4b2e83, 13: 0xe35205,
  14: 0x007a3d, 15: 0x7a1f2b,
};

export interface BallView {
  readonly mesh: THREE.Mesh;
  readonly shadow: THREE.Mesh;
  /** 配置モード中に「この球は移動可能」をふんわり光る円板で示すグロー。通常時は非表示。
   *  クロス直上（影と同レイヤ）に球の真下から外へ広がる加算ブレンド円板を敷く。 */
  readonly halo: THREE.Mesh;
}

const axis = new THREE.Vector3();

/** 練習用手球の赤ドット（6軸方向に固定）を子メッシュとして付ける。球と一緒に回る。 */
const addCueDots = (ball: THREE.Mesh): void => {
  const dotGeo = new THREE.CircleGeometry(ballRadius * 0.17, 16);
  const dotMat = new THREE.MeshStandardMaterial({ color: 0xcc1111, roughness: 0.4 });
  const dirs = [
    new THREE.Vector3(1, 0, 0),
    new THREE.Vector3(-1, 0, 0),
    new THREE.Vector3(0, 1, 0),
    new THREE.Vector3(0, -1, 0),
    new THREE.Vector3(0, 0, 1),
    new THREE.Vector3(0, 0, -1),
  ];
  const zAxis = new THREE.Vector3(0, 0, 1);
  for (const dir of dirs) {
    const dot = new THREE.Mesh(dotGeo, dotMat);
    dot.position.copy(dir).multiplyScalar(ballRadius * 1.002);
    dot.quaternion.setFromUnitVectors(zAxis, dir); // 面を外向きに
    ball.add(dot);
  }
};

export const createBallViews = (
  balls: readonly BallSnapshot[],
  scene: THREE.Scene,
): Map<number, BallView> => {
  const views = new Map<number, BallView>();
  const geometry = new THREE.IcosahedronGeometry(ballRadius, 3);
  const shadowGeo = new THREE.CircleGeometry(ballRadius * 0.92, 24);
  const shadowMat = new THREE.MeshBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.32,
    depthWrite: false,
  });
  // 配置可能ハロ: 球の足元に敷く加算ブレンドの暖色円板。配置モード中の「動かせる球」を示す。
  // ジオメトリ/マテリアルは全球で共有（毎フレーム opacity 更新は updateHaloPulse がまとめて行う）。
  // サイズは影（`ballRadius * 0.92`）の外側に控えめに広がる程度（=「ふんわり」を意識）。
  const haloGeo = new THREE.CircleGeometry(ballRadius * 1.3, 32);
  const haloMat = new THREE.MeshBasicMaterial({
    color: 0xffe27a, // 暖色イエロー（クロスの緑/グレーから視認しやすい）
    transparent: true,
    opacity: 0, // 非配置モード時は完全透明（visible=false でも安全側）
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  for (const b of balls) {
    let mesh: THREE.Mesh;
    if (b.id === 0) {
      // 手球: 無地の白＋赤ドット6点（練習球）
      const cueMat = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color(BALL_COLORS[0]),
        roughness: 0.16,
        metalness: 0,
        clearcoat: 0.4,
        clearcoatRoughness: 0.06,
        envMapIntensity: 0.28,
      });
      mesh = new THREE.Mesh(geometry, cueMat);
      addCueDots(mesh);
    } else {
      const color = new THREE.Color(BALL_COLORS[b.id] ?? 0xffffff);
      mesh = new THREE.Mesh(geometry, createProjectedMaterial(b.id, color));
    }
    mesh.castShadow = true;
    mesh.quaternion.random();
    mesh.position.set(b.x, b.y, b.z);
    scene.add(mesh);

    const shadow = new THREE.Mesh(shadowGeo, shadowMat);
    shadow.position.set(b.x, b.y, -ballRadius + 0.002);
    scene.add(shadow);

    // halo は通常非表示。配置モード中のみ updateHaloPulse で表示・点滅させる。
    // 影と z-fight しないよう影より僅かに下（クロスに近い）に置く。
    const halo = new THREE.Mesh(haloGeo, haloMat.clone());
    halo.position.set(b.x, b.y, -ballRadius + 0.0015);
    halo.visible = false;
    scene.add(halo);

    views.set(b.id, { mesh, shadow, halo });
  }
  return views;
};

/** 既存の球ビューをシーンから除去・破棄する（モード変更/球追加削除で再構築する前に呼ぶ）。 */
export const disposeBallViews = (
  views: Map<number, BallView>,
  scene: THREE.Scene,
): void => {
  for (const v of views.values()) {
    scene.remove(v.mesh);
    scene.remove(v.shadow);
    scene.remove(v.halo);
    if (Array.isArray(v.mesh.material)) v.mesh.material.forEach((m) => m.dispose());
    else v.mesh.material.dispose();
    v.mesh.geometry.dispose();
    if (Array.isArray(v.shadow.material)) v.shadow.material.forEach((m) => m.dispose());
    else v.shadow.material.dispose();
    v.shadow.geometry.dispose();
    if (Array.isArray(v.halo.material)) v.halo.material.forEach((m) => m.dispose());
    else v.halo.material.dispose();
    // halo の geometry は createBallViews 内で共有しているため個別 dispose しない
    // （次回 createBallViews で新規生成される）。
  }
  views.clear();
};

/**
 * 配置モード中の「移動可能な球」のハロをふんわり明滅させる。それ以外の球はハロを隠す。
 * - movableIds: 現在配置可能な球の id 集合（空セットなら全ハロを非表示）
 * - elapsedSec: ページロードからの経過秒（点滅の位相に使う）
 * 1.6 秒周期の sin で opacity を 0.18〜0.63 振動させ、ハードな点滅にならないよう振幅を抑える。
 */
const HALO_PULSE_PERIOD_SEC = 1.6;
const HALO_OPACITY_MIN = 0.18;
const HALO_OPACITY_AMP = 0.45;
export const updateHaloPulse = (
  views: Map<number, BallView>,
  movableIds: ReadonlySet<number>,
  elapsedSec: number,
): void => {
  // sin の位相を 0..1 へ正規化し、(sin+1)/2 で 0..1 のなめらか波形に。
  const phase = (elapsedSec % HALO_PULSE_PERIOD_SEC) / HALO_PULSE_PERIOD_SEC;
  const wave = (Math.sin(phase * Math.PI * 2) + 1) / 2;
  const opacity = HALO_OPACITY_MIN + wave * HALO_OPACITY_AMP;
  for (const [id, view] of views) {
    const movable = movableIds.has(id) && view.mesh.visible;
    view.halo.visible = movable;
    if (!movable) continue;
    const mat = view.halo.material as THREE.MeshBasicMaterial;
    mat.opacity = opacity;
    // halo は球の真下（クロス直上）に追従。syncBallViews と同フレームで位置を引き継ぐ。
    view.halo.position.set(view.mesh.position.x, view.mesh.position.y, -ballRadius + 0.0015);
  }
};

/** 物理スナップショットを描画へ反映。角速度(rvel)で見た目を回転 → スピンが見える。 */
export const syncBallViews = (
  balls: readonly BallSnapshot[],
  views: Map<number, BallView>,
  dt: number,
): void => {
  for (const b of balls) {
    const view = views.get(b.id);
    if (!view) continue;
    const visible = b.onTable || b.z > -ballRadius * 4;
    view.mesh.visible = visible;
    view.shadow.visible = visible && b.onTable;
    if (!visible) continue;

    view.mesh.position.set(b.x, b.y, b.z);
    view.shadow.position.set(b.x, b.y, -ballRadius + 0.002);

    const speed = Math.hypot(b.wx, b.wy, b.wz);
    if (speed > 1e-6 && dt > 0) {
      axis.set(b.wx / speed, b.wy / speed, b.wz / speed);
      view.mesh.rotateOnWorldAxis(axis, speed * dt);
    }
  }
};
